// =============================================================================
// Admin Analytics Dashboard — data contracts (PROA-1474)
// =============================================================================
// The UI's own vocabulary (report types, statuses, QA categories) and the
// Activation adapter contract. Reports-tab data comes straight from the
// generated `adminAnalytics` GraphQL types; `enumMaps.ts` maps between the two.
// =============================================================================

/** A resolved, absolute date range. ISO `yyyy-MM-dd` strings — what the BE API will speak. */
export interface DateRangeContract {
  /** Inclusive start, `yyyy-MM-dd`. */
  start: string;
  /** Inclusive end, `yyyy-MM-dd`. */
  end: string;
  /** Human label echoed back into the UI (e.g. "Last 7 Days"). Optional. */
  label?: string;
}

/** Report template families. `submitted`/`approved`-style statuses below are only meaningful for Catalight. */
export type TemplateType = "IA" | "RA" | "DB";

/**
 * QA flag categories — mirrors the curated `category_group` buckets the
 * backend normalizes raw flag categories into. `other` is a catch-all for any raw value
 * that doesn't land in one of the curated buckets (e.g. test/legacy data) —
 * included as a selectable filter, not just an internal fallback.
 */
export type QaCategory =
  | "document_completeness"
  | "goals"
  | "insurance_standards"
  | "custom"
  | "ccs_qa"
  | "clinical_content"
  | "other";

export const QA_CATEGORIES: QaCategory[] = [
  "document_completeness",
  "goals",
  "insurance_standards",
  "custom",
  "ccs_qa",
  "clinical_content",
  "other",
];

export const QA_CATEGORY_LABELS: Record<QaCategory, string> = {
  document_completeness: "Document Completeness",
  goals: "Goals",
  insurance_standards: "Insurance Standards",
  custom: "Custom",
  ccs_qa: "CCS QA",
  clinical_content: "Clinical Content",
  other: "Other",
};

export const TEMPLATE_TYPES: TemplateType[] = ["IA", "RA", "DB"];

/** Spelled-out report type names (no acronyms) for display in tables. */
export const TEMPLATE_TYPE_LABELS: Record<TemplateType, string> = {
  IA: "Initial Assessment",
  RA: "Reassessment",
  DB: "Diagnosis Report",
};

/**
 * The filter context handed to every adapter (Activation) call. `range` is
 * already the *effective* range for the widget (per-widget time override
 * resolved on top of the global range before it reaches the adapter).
 */
export interface DashboardFilters {
  range: DateRangeContract;
  /** Present only when the user has a comparison period active on the global filter. */
  comparisonRange?: DateRangeContract;
  /** IA / RA / DB report-type filter. Empty = all. */
  templateType: TemplateType[];
  /** Named template filter (e.g. "Catalight", "CCS"). Empty = all. */
  templateName: string[];
}

// ----- Report statuses --------------------------------------------------------

export type ReportStatus = "draft" | "complete" | "submitted" | "needs_revision" | "approved";

export const REPORT_STATUS_LABELS: Record<ReportStatus, string> = {
  draft: "Draft",
  complete: "Complete",
  submitted: "Submitted",
  needs_revision: "Needs Revision",
  approved: "Approved",
};

// ----- Activation view --------------------------------------------------------

export type InviteStatus = "sent" | "accepted" | "expired";

export const INVITE_STATUS_LABELS: Record<InviteStatus, string> = {
  sent: "Pending",
  accepted: "Accepted",
  expired: "Expired",
};

/** Which funnel bucket a provider falls into; also used to scope the drill-down lists. */
export type ActivationBucket = "invited" | "expired" | "loggedIn" | "notYetLoggedIn";

export interface InviteFunnel {
  invited: number;
  expired: number;
  loggedIn: number;
  notYetLoggedIn: number;
}

export interface ActivationProviderRow {
  providerId: string;
  name: string;
  email: string;
  /** `yyyy-MM-dd`. */
  invitedAt: string;
  status: InviteStatus;
  /** `yyyy-MM-dd` of last login, when the provider has ever logged in. */
  lastLoginAt?: string;
  hasLoggedIn: boolean;
}

// ----- Org-level product config -----------------------------------------------
// Describes what a specific org has licensed/enabled. Drives which filter
// options and widgets are visible — without changing the adapter interface.
// Today this is derived from AdminDataProvider flags; eventually it could
// come from a backend config endpoint.

export interface OrgConfig {
  /** Report template families this org has enabled. Drives the template filter pills. */
  enabledTemplates: TemplateType[];
  /** True for Catalight/ACES orgs — controls org-identity UI (badges, template tabs, Submission Trend). */
  isCatalightOrg: boolean;
  /** True when the `report-submit-to-payor` LD flag is on — gates submission-pipeline statuses in Reports by Status. */
  hasSubmissionIntegration: boolean;
}

export const DEFAULT_ORG_CONFIG: OrgConfig = {
  enabledTemplates: ["IA", "RA", "DB"],
  isCatalightOrg: false,
  hasSubmissionIntegration: false,
};

// ----- Adapter interface (Activation only) -----------------------------------

/**
 * Activation has no backend yet, so it still goes through a swappable adapter:
 * the live route passes `useGraphqlAdminAnalyticsAdapter()`, tests and the
 * default provider use `MockAdminAnalyticsAdapter`. The Reports tab reads the
 * `adminAnalytics` GraphQL resource directly (see `data/hooks.ts`).
 */
export interface AdminAnalyticsAdapter {
  getInviteFunnel(filters: DashboardFilters): Promise<InviteFunnel>;
  getActivationProviders(filters: DashboardFilters): Promise<ActivationProviderRow[]>;
}
